'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { ComponentProps } from 'react';
import { localizePath, splitLocale } from '@shared/locale';

type Props = ComponentProps<typeof Link>;

/**
 * `next/link` that keeps the visitor inside their locale.
 *
 * The marketing pages are served from one set of route files for both Turkish
 * and English, with the locale carried by a `/en` URL prefix. A plain
 * `<Link href="/pricing">` on `/en/faq` would therefore drop the reader back
 * into Turkish — and, worse, leave every English page without a single internal
 * link to another English page, orphaning the whole locale from crawlers.
 *
 * Prefixing happens off the current pathname, so callers keep writing plain
 * unprefixed paths and nothing has to thread a locale prop through the tree.
 * External URLs, anchors and `mailto:`/`tel:` links pass through untouched.
 */
export default function LocaleLink({ href, ...rest }: Props) {
  const pathname = usePathname() || '/';
  const { locale } = splitLocale(pathname);

  const localized =
    typeof href === 'string' && href.startsWith('/') ? localizePath(href, locale) : href;

  return <Link href={localized} {...rest} />;
}
